import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useHierarchicalCategory = (slugSegments: string[]) => {
  return useQuery({
    queryKey: ['hierarchical-category', ...slugSegments],
    queryFn: async () => {
      if (slugSegments.length === 0) return null;
      
      console.log('useHierarchicalCategory: Resolving hierarchy for:', slugSegments);
      
      // Start with Level 1
      const level1Slug = slugSegments[0];
      const { data: level1, error: level1Error } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', level1Slug)
        .eq('level', 1)
        .maybeSingle();
        
      if (level1Error) {
        console.error('useHierarchicalCategory: Error fetching level 1:', level1Error);
        throw level1Error;
      }
      
      if (!level1) {
        console.log('useHierarchicalCategory: Level 1 category not found:', level1Slug);
        throw new Error('Category not found');
      }
      
      console.log('useHierarchicalCategory: Found level 1:', level1);
      
      if (slugSegments.length === 1) return level1;
      
      // Level 2
      const level2Slug = slugSegments[1];
      const { data: level2, error: level2Error } = await supabase
        .from('categories')
        .select(`
          *,
          parent_category:parent_category_id(*)
        `)
        .eq('slug', level2Slug)
        .eq('parent_category_id', level1.id)
        .maybeSingle();
        
      if (level2Error) {
        console.error('useHierarchicalCategory: Error fetching level 2:', level2Error);
        throw level2Error;
      }
      
      if (!level2) {
        console.log('useHierarchicalCategory: Level 2 category not found:', level2Slug);
        throw new Error('Category not found');
      }
      
      console.log('useHierarchicalCategory: Found level 2:', level2);
      
      if (slugSegments.length === 2) return level2;
      
      // Level 3
      const level3Slug = slugSegments[2];
      const { data: level3, error: level3Error } = await supabase
        .from('categories')
        .select(`
          *,
          parent_category:parent_category_id(
            *,
            parent_category:parent_category_id(*)
          )
        `)
        .eq('slug', level3Slug)
        .eq('parent_category_id', level2.id)
        .maybeSingle();
        
      if (level3Error) {
        console.error('useHierarchicalCategory: Error fetching level 3:', level3Error);
        throw level3Error;
      }
      
      if (!level3) {
        console.log('useHierarchicalCategory: Level 3 category not found:', level3Slug);
        throw new Error('Category not found');
      }
      
      console.log('useHierarchicalCategory: Found level 3:', level3);
      
      return level3;
    },
    enabled: slugSegments.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
